import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Send, MessageSquare, ChevronLeft, UserPlus, Check, X } from "lucide-react";
import api from "@/lib/api";
import { Avatar, timeAgo } from "@/components/common";
import { PageHeader } from "@/components/PageHeader";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function Thread({ conversationId }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [other, setOther] = useState(null);
  const [text, setText] = useState("");
  const seqRef = useRef(0);
  const bottomRef = useRef(null);

  const poll = useCallback(async () => {
    try {
      const r = await api.get(`/conversations/${conversationId}/messages`, { params: { since: seqRef.current } });
      setOther(r.data.other);
      if (r.data.messages.length) {
        seqRef.current = r.data.messages[r.data.messages.length - 1].seq;
        setMessages((prev) => {
          const seen = new Set(prev.map((m) => m.message_id));
          const fresh = r.data.messages.filter((m) => !seen.has(m.message_id));
          return fresh.length ? [...prev, ...fresh] : prev;
        });
      }
    } catch (e) {
      if (e.response?.status === 403) navigate("/messages");
    }
  }, [conversationId, navigate]);

  useEffect(() => {
    setMessages([]); seqRef.current = 0;
    poll();
    const t = setInterval(poll, 2500);
    return () => clearInterval(t);
  }, [conversationId, poll]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, [messages]);

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setText("");
    try {
      const r = await api.post(`/conversations/${conversationId}/messages`, { text: t });
      seqRef.current = Math.max(seqRef.current, r.data.seq);
      setMessages((prev) => (prev.some((m) => m.message_id === r.data.message_id) ? prev : [...prev, r.data]));
    } catch (e) {}
  };

  return (
    <div className="border border-border bg-card rounded-xl flex flex-col h-[70vh]" data-testid="dm-thread">
      <div className="px-5 py-3 border-b border-border flex items-center gap-3">
        <button onClick={() => navigate("/messages")} className="md:hidden text-muted-foreground"><ChevronLeft size={18} /></button>
        {other && (
          <Link to={`/members/${other.user_id}`} className="flex items-center gap-2.5">
            <Avatar src={other.picture} name={other.name} size={34} />
            <span className="font-semibold">{other.name}</span>
          </Link>
        )}
      </div>
      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        {messages.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-10">No messages yet. Say hello 👋</p>
        ) : messages.map((m) => {
          const mine = m.sender_id === user?.user_id;
          return (
            <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`dm-msg-${m.message_id}`}>
              <div className={`max-w-[75%] ${mine ? "text-right" : ""}`}>
                <div className={`text-sm px-3 py-2 rounded-lg whitespace-pre-wrap break-words ${mine ? "bg-cobalt text-white" : "bg-secondary"}`}>{m.text}</div>
                <div className="text-[11px] text-muted-foreground mt-1">{timeAgo(m.created_at)}</div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>
      <div className="p-3 border-t border-border flex items-center gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Write a message…" data-testid="dm-input"
          className="flex-1 bg-transparent border border-border rounded-md px-3 py-2 text-sm outline-none focus:border-cobalt transition-colors" />
        <button onClick={send} data-testid="dm-send" className="bg-cobalt text-white rounded-md p-2.5 hover:bg-gold-hover transition-colors"><Send size={16} /></button>
      </div>
    </div>
  );
}

export default function Messages() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const [convos, setConvos] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    api.get("/conversations").then((r) => setConvos(r.data)).finally(() => setLoading(false));
    api.get("/connections").then((r) => setRequests(r.data.incoming || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [load]);

  const accept = async (uid) => {
    try {
      const r = await api.post(`/connections/${uid}/accept`);
      toast.success("Connected — say hello!");
      load();
      if (r.data.conversation_id) navigate(`/messages/${r.data.conversation_id}`);
    } catch (e) { toast.error("Could not accept"); }
  };
  const decline = async (uid) => {
    try { await api.post(`/connections/${uid}/decline`); setRequests((p) => p.filter((x) => x.user.user_id !== uid)); }
    catch (e) { toast.error("Could not decline"); }
  };

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Messages" title="Your conversations"
        subtitle="Warm intros and ongoing chats with your connections." />

      {requests.length > 0 && (
        <div className="border border-cobalt/40 bg-secondary/40 rounded-xl p-4" data-testid="connection-requests">
          <div className="eyebrow text-cobalt mb-3 flex items-center gap-1.5"><UserPlus size={13} /> Connection requests · {requests.length}</div>
          <div className="space-y-3">
            {requests.map((req) => (
              <div key={req.connection_id} className="flex items-start gap-3" data-testid={`request-${req.user.user_id}`}>
                <Link to={`/members/${req.user.user_id}`}><Avatar src={req.user.picture} name={req.user.name} size={40} /></Link>
                <div className="min-w-0 flex-1">
                  <Link to={`/members/${req.user.user_id}`} className="text-sm font-semibold hover:text-cobalt transition-colors">{req.user.name}</Link>
                  {req.note && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">"{req.note}"</p>}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => accept(req.user.user_id)} data-testid={`request-accept-${req.user.user_id}`}
                    className="inline-flex items-center gap-1 bg-cobalt text-white rounded-md px-3 py-1.5 text-xs font-semibold hover:bg-gold-hover transition-colors"><Check size={13} /> Accept</button>
                  <button onClick={() => decline(req.user.user_id)} data-testid={`request-decline-${req.user.user_id}`}
                    className="inline-flex items-center gap-1 border border-border rounded-md px-3 py-1.5 text-xs hover:bg-secondary/60 transition-colors"><X size={13} /> Decline</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-[320px_1fr] gap-5">
        <div className={`space-y-2 ${conversationId ? "hidden md:block" : ""}`} data-testid="dm-list">
          {loading ? (
            <div className="text-sm text-muted-foreground eyebrow py-6 text-center">Loading…</div>
          ) : convos.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center border border-dashed border-border rounded-xl">
              No conversations yet. Connect with people from Discover.
            </div>
          ) : convos.map((c) => (
            <Link key={c.conversation_id} to={`/messages/${c.conversation_id}`} data-testid={`dm-convo-${c.conversation_id}`}
              className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${conversationId === c.conversation_id ? "border-cobalt bg-secondary/50" : "border-border hover:bg-secondary/40"}`}>
              <Avatar src={c.other.picture} name={c.other.name} size={40} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold truncate">{c.other.name}</span>
                  {c.unread > 0 && <span className="shrink-0 bg-cobalt text-white text-[10px] font-mono rounded-full px-1.5 py-0.5" data-testid={`dm-unread-${c.conversation_id}`}>{c.unread}</span>}
                </div>
                <div className="text-xs text-muted-foreground truncate">{c.last_message || "…"}</div>
              </div>
            </Link>
          ))}
        </div>
        <div className={`${conversationId ? "" : "hidden md:flex"}`}>
          {conversationId ? <Thread conversationId={conversationId} /> : (
            <div className="hidden md:flex flex-col items-center justify-center h-[70vh] border border-dashed border-border rounded-xl text-muted-foreground">
              <MessageSquare size={30} className="mb-3 opacity-60" />
              <p className="text-sm">Select a conversation to start chatting.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
